import { ensureFileHandle } from "./downloadFilesystem";
import {
  cancelDownloadJob,
  getDownloadJob,
  recalculateDownloadJobProgress,
  updateDownloadChunk,
  updateDownloadFile,
  updateDownloadJob,
} from "./downloadPersistence";

function getDefaultFetch() {
  return typeof fetch !== "undefined" ? fetch : undefined;
}

function validateInputs({
  db,
  jobId,
  rootDirectoryHandle,
  getSignedUrl,
  fetchImpl,
}) {
  if (!db) {
    throw new Error("A download persistence database is required.");
  }

  if (!jobId) {
    throw new Error("A download job id is required.");
  }

  if (!rootDirectoryHandle) {
    throw new Error("A root download directory handle is required.");
  }

  if (typeof getSignedUrl !== "function") {
    throw new Error("A signed URL provider is required.");
  }

  if (typeof fetchImpl !== "function") {
    throw new Error("A fetch implementation is required.");
  }
}

function createTimestamp() {
  return new Date().toISOString();
}

async function writeFileBytes({ writable, bytes, position }) {
  await writable.write({
    type: "write",
    position,
    data: bytes,
  });
}

function getChunksForFile(chunks, fileId) {
  return (chunks || [])
    .filter((chunk) => chunk.fileRecordId === fileId)
    .sort((a, b) => a.start - b.start);
}

function getCompletedBytes(chunks) {
  return chunks
    .filter((chunk) => chunk.status === "complete")
    .reduce((total, chunk) => total + (Number(chunk.size) || 0), 0);
}

function isDownloadFileComplete({ file, fileChunks }) {
  if (fileChunks.length > 0) {
    return fileChunks.every((chunk) => chunk.status === "complete");
  }

  return Number(file && file.size) === 0;
}

function getRangeHeader(chunk) {
  return `bytes=${chunk.start}-${chunk.end}`;
}

function validateDownloadFile(file) {
  const fileId = file && file.id ? file.id : "unknown";

  if (!file || !file.uri) {
    throw new Error(`Download file ${fileId} is missing uri.`);
  }

  if (!file.safeRelativePath) {
    throw new Error(`Download file ${fileId} is missing safeRelativePath.`);
  }
}

function validateChunk(chunk) {
  if (
    !chunk ||
    typeof chunk.start !== "number" ||
    typeof chunk.end !== "number"
  ) {
    throw new Error("Download chunk must include numeric start and end bytes.");
  }
}

function validateChunkSize({ chunk, bytes }) {
  if (bytes.byteLength !== chunk.size) {
    throw new Error(
      `Chunk download size mismatch for ${chunk.id}. Expected ${chunk.size}, received ${bytes.byteLength}.`
    );
  }
}

async function closeWritableQuietly(writable) {
  if (!writable || typeof writable.close !== "function") {
    return;
  }

  try {
    await writable.close();
  } catch (error) {
    // Preserve the original download error when cleanup also fails.
  }
}

function isAbortError(error, abortSignal) {
  return Boolean(
    (abortSignal && abortSignal.aborted) ||
      (error && (error.name === "AbortError" || error.code === "ABORT_ERR"))
  );
}

async function markJobRunning({ db, job }) {
  const updatedAt = createTimestamp();

  await updateDownloadJob({
    db,
    job: {
      ...job,
      status: "running",
      updatedAt,
    },
  });

  return {
    ...job,
    status: "running",
    updatedAt,
  };
}

async function markJobPaused({ db, job }) {
  const updatedAt = createTimestamp();

  await updateDownloadJob({
    db,
    job: {
      ...job,
      status: "paused",
      updatedAt,
    },
  });
}

export async function runDownloadJob({
  db,
  jobId,
  rootDirectoryHandle,
  getSignedUrl,
  fetchImpl = getDefaultFetch(),
  abortSignal,
  isCancellationRequested = () => false,
}) {
  validateInputs({
    db,
    jobId,
    rootDirectoryHandle,
    getSignedUrl,
    fetchImpl,
  });

  const downloadJob = await getDownloadJob({ db, jobId });

  if (!downloadJob) {
    throw new Error(`Download job not found: ${jobId}`);
  }

  const { job, files, chunks } = downloadJob;

  if (job.status === "cancelled") {
    return {
      jobId,
      filesCompleted: 0,
      bytesDownloaded: 0,
      cancelled: true,
    };
  }

  let latestJob = await markJobRunning({ db, job });
  let filesCompleted = 0;
  let bytesDownloaded = 0;
  const completedFileIds = new Set();

  for (const file of files || []) {
    if (file.status === "complete") {
      continue;
    }

    validateDownloadFile(file);

    const fileChunks = getChunksForFile(chunks, file.id);

    if (fileChunks.length === 0 && Number(file.size) > 0) {
      throw new Error(`Download file ${file.id} has no chunks.`);
    }

    let currentFile = {
      ...file,
      completedBytes: getCompletedBytes(fileChunks),
    };
    const pendingChunks = fileChunks.filter(
      (chunk) => chunk.status !== "complete"
    );
    let fileHandle;
    let writable;
    let signedUrl;
    let activeChunk = null;
    const finishCancelled = async () => {
      await closeWritableQuietly(writable);

      if (activeChunk && activeChunk.status !== "complete") {
        await updateDownloadChunk({
          db,
          chunk: {
            ...activeChunk,
            status: "pending",
            updatedAt: createTimestamp(),
          },
        });
      }

      const cancelledJob = await cancelDownloadJob({ db, jobId });

      return {
        jobId,
        filesCompleted,
        bytesDownloaded,
        cancelled: true,
        job: cancelledJob,
      };
    };

    if (pendingChunks.length > 0) {
      ({ fileHandle } = await ensureFileHandle({
        rootDirectoryHandle,
        safeRelativePath: file.safeRelativePath,
      }));
      writable = await fileHandle.createWritable({ keepExistingData: true });
      signedUrl = await getSignedUrl({
        identityId: file.identityId || job.identityId,
        key: file.uri,
      });
    }

    try {
      for (const chunk of pendingChunks) {
        validateChunk(chunk);

        const runningAt = createTimestamp();
        const runningChunk = {
          ...chunk,
          status: "running",
          updatedAt: runningAt,
        };

        activeChunk = runningChunk;
        await updateDownloadChunk({
          db,
          chunk: runningChunk,
        });

        if (isCancellationRequested()) {
          return await finishCancelled();
        }

        const fetchOptions = {
          headers: {
            Range: getRangeHeader(chunk),
          },
          ...(abortSignal ? { signal: abortSignal } : {}),
        };
        const response = await fetchImpl(signedUrl, fetchOptions);

        if (isCancellationRequested()) {
          return await finishCancelled();
        }

        if (!response || response.status !== 206) {
          const status =
            response && response.status ? response.status : "unknown";

          throw new Error(
            `Chunk download failed for ${chunk.id}. Expected 206, received ${status}.`
          );
        }

        const bytes = await response.arrayBuffer();

        if (isCancellationRequested()) {
          return await finishCancelled();
        }

        validateChunkSize({ chunk, bytes });

        const completedAt = createTimestamp();

        await writeFileBytes({ writable, bytes, position: chunk.start });

        const updatedChunk = {
          ...runningChunk,
          status: "complete",
          completedAt,
          updatedAt: completedAt,
        };

        await updateDownloadChunk({
          db,
          chunk: updatedChunk,
        });
        activeChunk = null;

        const chunkIndex = fileChunks.findIndex(
          (fileChunk) => fileChunk.id === chunk.id
        );

        if (chunkIndex !== -1) {
          fileChunks[chunkIndex] = updatedChunk;
        }

        currentFile = {
          ...currentFile,
          completedBytes: getCompletedBytes(fileChunks),
          updatedAt: completedAt,
        };

        await updateDownloadFile({
          db,
          file: currentFile,
        });

        latestJob =
          (await recalculateDownloadJobProgress({ db, jobId })) || latestJob;
        bytesDownloaded += bytes.byteLength;
      }
    } catch (error) {
      if (isAbortError(error, abortSignal)) {
        if (isCancellationRequested()) {
          return await finishCancelled();
        }

        if (activeChunk && activeChunk.status !== "complete") {
          await updateDownloadChunk({
            db,
            chunk: {
              ...activeChunk,
              status: "pending",
              updatedAt: createTimestamp(),
            },
          });
        }

        await closeWritableQuietly(writable);
        await markJobPaused({ db, job: latestJob });

        return {
          jobId,
          filesCompleted,
          bytesDownloaded,
          paused: true,
        };
      }

      await closeWritableQuietly(writable);

      if (activeChunk && activeChunk.status !== "complete") {
        await updateDownloadChunk({
          db,
          chunk: {
            ...activeChunk,
            status: "failed",
            error: error && error.message ? error.message : undefined,
            updatedAt: createTimestamp(),
          },
        });
      }

      throw error;
    }

    if (writable) {
      await writable.close();
    }

    if (isCancellationRequested()) {
      return await finishCancelled();
    }

    if (isDownloadFileComplete({ file, fileChunks })) {
      const completedAt = createTimestamp();

      currentFile = {
        ...currentFile,
        status: "complete",
        completedAt,
        updatedAt: completedAt,
      };

      await updateDownloadFile({
        db,
        file: currentFile,
      });
      latestJob =
        (await recalculateDownloadJobProgress({ db, jobId })) || latestJob;
      filesCompleted += 1;
      completedFileIds.add(file.id);
    }
  }

  const loadedFiles = files || [];

  if (isCancellationRequested()) {
    const cancelledJob = await cancelDownloadJob({ db, jobId });

    return {
      jobId,
      filesCompleted,
      bytesDownloaded,
      cancelled: true,
      job: cancelledJob,
    };
  }

  const allLoadedFilesComplete =
    loadedFiles.length > 0 &&
    loadedFiles.every(
      (file) => file.status === "complete" || completedFileIds.has(file.id)
    );

  if (allLoadedFilesComplete) {
    latestJob =
      (await recalculateDownloadJobProgress({ db, jobId })) || latestJob;

    const completedAt = createTimestamp();

    await updateDownloadJob({
      db,
      job: {
        ...latestJob,
        status: "complete",
        completedAt,
        updatedAt: completedAt,
      },
    });
  }

  return {
    jobId,
    filesCompleted,
    bytesDownloaded,
  };
}

export function createDownloadJobSession({
  db,
  jobId,
  rootDirectoryHandle,
  getSignedUrl,
  fetchImpl = getDefaultFetch(),
}) {
  let abortController = null;
  let currentRun = null;
  let cancelRequested = false;

  function startRun() {
    cancelRequested = false;
    abortController = new AbortController();
    currentRun = runDownloadJob({
      db,
      jobId,
      rootDirectoryHandle,
      getSignedUrl,
      fetchImpl,
      abortSignal: abortController.signal,
      isCancellationRequested: () => cancelRequested,
    }).finally(() => {
      abortController = null;
      currentRun = null;
    });

    return currentRun;
  }

  return {
    start() {
      return currentRun || startRun();
    },
    resume() {
      return currentRun || startRun();
    },
    pause() {
      if (abortController) {
        abortController.abort();
      }

      return currentRun;
    },
    cancel() {
      cancelRequested = true;

      if (abortController) {
        abortController.abort();
      }

      return currentRun;
    },
  };
}
